/**
 * Contentful securityHubI18n entry key. The `value` field is a JSON array of template copy objects
 * (see AmplifierTrainingTemplateCopyFromI18n).
 */
export const AMPLIFIER_COMPLIANCE_TRAINING_ACTION_TEMPLATES_I18N_KEY =
    'AMPLIFIER_COMPLIANCE_TRAINING_ACTION_TEMPLATES';

export type AmplifierTrainingTemplateKey = 'scheduled' | 'module' | 'custom';

export type AmplifierTrainingTemplateCopyFromI18n = {
    key: AmplifierTrainingTemplateKey;
    title: string;
    cardSubtitle: string;
    description: string;
    actionNamePlaceholder: string;
    actionNameHelper: string;
    descriptionPlaceholder?: string;
};

const TEMPLATE_KEYS: AmplifierTrainingTemplateKey[] = ['scheduled', 'module', 'custom'];

const isTemplateKey = (value: unknown): value is AmplifierTrainingTemplateKey =>
    typeof value === 'string' && TEMPLATE_KEYS.includes(value as AmplifierTrainingTemplateKey);

const isCopyEntry = (value: unknown): value is AmplifierTrainingTemplateCopyFromI18n => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const entry = value as Record<string, unknown>;
    return (
        isTemplateKey(entry.key) &&
        typeof entry.title === 'string' &&
        typeof entry.cardSubtitle === 'string' &&
        typeof entry.description === 'string' &&
        typeof entry.actionNamePlaceholder === 'string' &&
        typeof entry.actionNameHelper === 'string' &&
        (entry.descriptionPlaceholder === undefined || typeof entry.descriptionPlaceholder === 'string')
    );
};

/** Parses Contentful i18n JSON array; returns entries keyed by template key. Invalid input yields {}. */
export const parseAmplifierComplianceTrainingActionTemplatesFromI18n = (
    raw: string | null | undefined,
): Partial<Record<AmplifierTrainingTemplateKey, AmplifierTrainingTemplateCopyFromI18n>> => {
    const trimmed = raw?.trim();
    if (!trimmed) {
        return {};
    }
    try {
        const parsed: unknown = JSON.parse(trimmed);
        if (!Array.isArray(parsed)) {
            return {};
        }
        return parsed.reduce<Partial<Record<AmplifierTrainingTemplateKey, AmplifierTrainingTemplateCopyFromI18n>>>(
            (acc, item) => {
                if (isCopyEntry(item)) {
                    acc[item.key] = item;
                }
                return acc;
            },
            {},
        );
    } catch {
        return {};
    }
};
